import { useRef, type KeyboardEvent } from "react";

import { cn } from "./cn";
import {
  segmentedActiveClassName,
  segmentedItemClassName,
  segmentedTrackClassName,
} from "./segmentedStyles";

export interface SegmentedOption {
  id: string;
  label: string;
}

export interface SegmentedProps {
  options: SegmentedOption[];
  value: string;
  onChange: (id: string) => void;
  /** Accessible name of the group. */
  label: string;
  className?: string;
}

/** Single-choice control with radiogroup semantics and roving focus. */
export function Segmented({
  options,
  value,
  onChange,
  label,
  className,
}: SegmentedProps) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const move = (from: number, step: number) => {
    const next = (from + step + options.length) % options.length;
    const option = options[next];
    if (!option) return;
    onChange(option.id);
    refs.current[next]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent, index: number) => {
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      move(index, 1);
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      move(index, -1);
    }
  };

  const hasSelection = options.some((option) => option.id === value);

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(segmentedTrackClassName, className)}
    >
      {options.map((option, index) => {
        const selected = option.id === value;
        return (
          <button
            key={option.id}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected || (!hasSelection && index === 0) ? 0 : -1}
            onClick={() => {
              onChange(option.id);
            }}
            onKeyDown={(event) => {
              onKeyDown(event, index);
            }}
            className={cn(
              segmentedItemClassName,
              selected && segmentedActiveClassName,
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
